import { useState, useEffect } from "react";
import api from "../../../utils/api";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    OutlinedInput,
    FormHelperText,
    Button,
    Box,
    FormControl,
    FormLabel,
    CircularProgress,
    Tabs,
    Tab,
    Avatar,
} from "@mui/material";

const languages = ['hy', 'en', 'ru'];
const languageLabels = {
    'hy': 'Armenian',
    'en': 'English',
    'ru': 'Russian'
};

export default function GalleryModal({ open, onClose, row = null }) {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [form, setForm] = useState(() => {
        const initial = {};
        languages.forEach(lang => {
            initial[`alt_${lang}`] = '';
        });
        initial.order = 0;
        return initial;
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);
            setErrors({});

            await api.put(`admin/gallery/${row.id}`, form);

            onClose(true);
        } catch (e) {
            if (e.errors) {
                setErrors(e.errors);
            } else if (e.response?.data?.errors) {
                setErrors(e.response.data.errors);
            }
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (row) {
            const newForm = {};
            languages.forEach(lang => {
                newForm[`alt_${lang}`] = row[`alt_${lang}`] || '';
            });
            newForm.order = row.order || 0;
            setForm(newForm);
        } else {
            const initial = {};
            languages.forEach(lang => {
                initial[`alt_${lang}`] = '';
            });
            initial.order = 0;
            setForm(initial);
        }
        setErrors({});
        setSaving(false);
        setActiveTab(0);
    }, [row, open]);

    const currentLanguage = languages[activeTab];

    if (!row) {
        return null; // This modal is only for editing
    }

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
            <DialogTitle>Edit Gallery Item</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    {/* Image Preview */}
                    {row.url && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                            <Avatar
                                variant="rounded"
                                src={row.url}
                                sx={{ width: 200, height: 200 }}
                            />
                        </Box>
                    )}

                    {/* Language Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                            {languages.map((lang, index) => (
                                <Tab key={lang} label={languageLabels[lang]} />
                            ))}
                        </Tabs>
                    </Box>

                    {/* Alt Text for Current Language */}
                    <FormControl error={!!errors[`alt_${currentLanguage}`]?.[0]}>
                        <FormLabel htmlFor={`alt_${currentLanguage}`}>Alt Text ({languageLabels[currentLanguage]})</FormLabel>
                        <OutlinedInput
                            name={`alt_${currentLanguage}`}
                            fullWidth
                            value={form[`alt_${currentLanguage}`] || ''}
                            onChange={handleChange}
                            placeholder={`Enter alt text in ${languageLabels[currentLanguage]}`}
                        />
                        {errors[`alt_${currentLanguage}`] && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors[`alt_${currentLanguage}`]}>
                                {errors[`alt_${currentLanguage}`][0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    {/* Order */}
                    <FormControl error={!!errors.order?.[0]}>
                        <FormLabel htmlFor="order">Order</FormLabel>
                        <OutlinedInput
                            name="order"
                            type="number"
                            fullWidth
                            value={form.order || 0}
                            onChange={handleChange}
                        />
                        {errors.order && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.order}>
                                {errors.order[0]}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button
                    sx={{ color: '#fff' }}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving ? <CircularProgress size={24} /> : "Save Changes"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
